import { Controller } from "@hotwired/stimulus";

// Drives the admin "Bulk Grant Access" modal on All Payments (TMS-1605):
// Recipients → Products → Notify, then "Preview recipients" posts the whole form (the
// pasted list or the uploaded file) and opens the saved batch, whose page is the Review
// step. Nothing is granted from here. The fields are real form inputs, so the post is
// just their FormData; the modal fully resets on close.
export default class extends Controller {
  static targets = [
    "form", "stepPill", "step",
    "error", "errorMessage",
    "pastePanel", "pasteInput", "filePanel", "fileInput",
    "product", "amount",
    "notifyDetail", "channel",
    "backBtn", "nextBtn", "submitBtn"
  ];

  static values = { url: String };

  static LAST_STEP = 3;

  connect() {
    this.currentStep = 1;
    this._boundReset = this._reset.bind(this);
    this.element.addEventListener("hidden.bs.modal", this._boundReset);
    this._showStep();
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._boundReset);
  }

  // ── Step navigation ──────────────────────────────────────────────────

  nextStep() {
    const error = this._validateStep(this.currentStep);
    if (error) {
      this._showError(error);
      return;
    }
    this._hideError();
    if (this.currentStep < this.constructor.LAST_STEP) {
      this.currentStep += 1;
      this._showStep();
    }
  }

  prevStep() {
    this._hideError();
    if (this.currentStep > 1) {
      this.currentStep -= 1;
      this._showStep();
    }
  }

  _showStep() {
    const last = this.constructor.LAST_STEP;
    this.stepTargets.forEach((el) => {
      el.classList.toggle("d-none", Number(el.dataset.step) !== this.currentStep);
    });
    this.stepPillTargets.forEach((pill) => {
      const step = Number(pill.dataset.step);
      pill.classList.toggle("is-active", step === this.currentStep);
      pill.classList.toggle("is-complete", step < this.currentStep);
      if (step === this.currentStep) {
        pill.setAttribute("aria-current", "step");
      } else {
        pill.removeAttribute("aria-current");
      }
    });

    this.backBtnTarget.classList.toggle("d-none", this.currentStep === 1);
    this.nextBtnTarget.classList.toggle("d-none", this.currentStep === last);
    this.submitBtnTarget.classList.toggle("d-none", this.currentStep !== last);
  }

  _validateStep(step) {
    if (step === 1) {
      if (this._source() === "file") {
        if (!this.fileInputTarget.files.length) return "Choose a CSV or .xlsx file.";
      } else if (!this.pasteInputTarget.value.includes("@")) {
        return "Paste at least one email address.";
      }
    }
    if (step === 2) {
      if (!this.productTargets.some((input) => input.checked)) return "Select at least one payment product.";
      const amount = this.hasAmountTarget ? this.amountTarget.value.trim() : "";
      if (amount && !(Number(amount) >= 0)) return "Amount invoiced must be a number of 0 or more.";
    }
    if (step === 3 && this._notifyEnabled() && !this.channelTargets.some((input) => input.checked)) {
      return "Choose at least one way to notify them, or pick No.";
    }
    return null;
  }

  // ── Field toggles ────────────────────────────────────────────────────

  onSourceChange() {
    const file = this._source() === "file";
    this.pastePanelTarget.classList.toggle("d-none", file);
    this.filePanelTarget.classList.toggle("d-none", !file);
    this._hideError();
  }

  onNotifyChange() {
    this.notifyDetailTarget.classList.toggle("d-none", !this._notifyEnabled());
    this._hideError();
  }

  // ── Submit ───────────────────────────────────────────────────────────

  async submit(event) {
    event.preventDefault();
    for (let step = 1; step <= this.constructor.LAST_STEP; step += 1) {
      const error = this._validateStep(step);
      if (error) {
        this.currentStep = step;
        this._showStep();
        this._showError(error);
        return;
      }
    }

    // Only the chosen source goes up: a pasted list left behind after switching to a
    // file (or the reverse) must not ride along.
    const data = new FormData(this.formTarget);
    if (this._source() === "file") {
      data.delete("bulk_access_grant[pasted_text]");
    } else {
      data.delete("bulk_access_grant[file]");
    }

    this.submitBtnTarget.disabled = true;
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: data
      });
      const payload = await response.json();
      if (payload.success) {
        window.location.assign(payload.redirect_url);
        return;
      }
      this._showError((payload.errors || ["Could not read the list."]).join(", "));
    } catch (e) {
      this._showError("Something went wrong. Please try again.");
    }
    this.submitBtnTarget.disabled = false;
  }

  // ── Reset ────────────────────────────────────────────────────────────

  _reset() {
    this.formTarget.reset();
    this.currentStep = 1;
    this.onSourceChange();
    this.onNotifyChange();
    this.submitBtnTarget.disabled = false;
    this._hideError();
    this._showStep();
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  _source() {
    return this.formTarget.querySelector('input[name="bulk_access_grant[source]"]:checked')?.value || "paste";
  }

  _notifyEnabled() {
    return this.formTarget.querySelector('input[name="bulk_access_grant[notify][enabled]"]:checked')?.value === "1";
  }

  _showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorTarget.classList.remove("d-none");
  }

  _hideError() {
    this.errorTarget.classList.add("d-none");
  }
}
